import { Link } from 'react-router-dom';
import { Receipt, FileText, AlertCircle, CheckCircle2, Clock, FileX, ArrowRight } from 'lucide-react';

const PATHWAY_META = {
  cod_checks: {
    title: 'COD Checks',
    icon: <Receipt className="w-6 h-6" />,
    description: 'Checks scanned from Google Drive for this date.',
  },
  statement_vendors: {
    title: 'Statement Vendors',
    icon: <FileText className="w-6 h-6" />,
    description: 'Statement reconciliation (XL Parts, Advance, O’Reilly, AutoZone).',
  },
};

const STATUS_META = {
  issues: { label: '(Issues)', cls: 'text-[var(--color-danger-700)] font-bold', icon: <AlertCircle className="w-4 h-4" /> },
  okay: { label: '(OKAY)', cls: 'text-[var(--color-recovery-700)] font-bold', icon: <CheckCircle2 className="w-4 h-4" /> },
  awaiting_review: { label: '(Needs Review)', cls: 'text-[var(--color-action-700)] font-bold', icon: <Clock className="w-4 h-4" /> },
  not_scanned: { label: '(Scans Missing)', cls: 'text-[var(--color-danger-700)] font-bold', icon: <FileX className="w-4 h-4" /> },
  not_implemented: { label: '(Coming Soon)', cls: 'text-[var(--color-warm-500)] italic', icon: null },
};

export default function PathwayCard({ pathway, summary, basePath, disabled = false }) {
  const meta = PATHWAY_META[pathway];
  const status = summary?.status || 'okay';
  const statusMeta = STATUS_META[status];
  const isDisabled = disabled || status === 'not_implemented';

  const inner = (
    <div className={`p-6 rounded-xl border bg-white transition-all ${
      isDisabled
        ? 'border-[var(--color-warm-200)] opacity-50 cursor-not-allowed'
        : 'border-[var(--color-warm-200)] hover:border-[var(--color-warm-300)] hover:shadow-md cursor-pointer'
    }`}>
      <div className="flex items-start gap-4">
        <div className={`p-3 rounded-lg ${
          status === 'issues' || status === 'not_scanned' ? 'bg-[var(--color-danger-50)] text-[var(--color-danger-700)]' :
          status === 'okay' ? 'bg-[var(--color-recovery-50)] text-[var(--color-recovery-700)]' :
          status === 'awaiting_review' ? 'bg-[var(--color-action-50)] text-[var(--color-action-700)]' :
          'bg-[var(--color-warm-100)] text-[var(--color-warm-500)]'
        }`}>
          {meta.icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h3 className="text-[16px] font-bold text-[var(--color-warm-900)]">{meta.title}</h3>
            <span className={`text-[13px] flex items-center gap-1 ${statusMeta.cls}`}>
              {statusMeta.icon}
              {statusMeta.label}
            </span>
          </div>
          <p className="text-[13px] text-[var(--color-warm-600)] mt-1">{meta.description}</p>
          {summary && status !== 'not_implemented' && status !== 'not_scanned' && (
            <div className="mt-3 flex items-center gap-4 text-[12px] text-[var(--color-warm-600)]">
              {summary.needs_action > 0 && (
                <span><span className="font-bold text-[var(--color-danger-700)]">{summary.needs_action}</span> need action</span>
              )}
              {summary.awaiting_review > 0 && (
                <span><span className="font-bold text-[var(--color-action-700)]">{summary.awaiting_review}</span> awaiting review</span>
              )}
              {summary.passed > 0 && (
                <span><span className="font-bold text-[var(--color-recovery-700)]">{summary.passed}</span> passed</span>
              )}
              {summary.total === 0 && (
                <span className="italic">No items</span>
              )}
            </div>
          )}
          {status === 'not_scanned' && (
            <p className="text-[12px] text-[var(--color-danger-600)] mt-2 italic">
              No scan yet for this date. Pipeline runs nightly at 3:30 AM.
            </p>
          )}
        </div>
        {!isDisabled && (
          <ArrowRight className="w-5 h-5 text-[var(--color-warm-400)] shrink-0 mt-1" />
        )}
      </div>
    </div>
  );

  if (isDisabled) return inner;
  // Always route through Document Review first — if empty, the review page
  // shows "No Items for Document Review!" with a Continue button.
  const to = `${basePath}/${pathway.replace('_', '-')}/review`;
  return <Link to={to} className="block">{inner}</Link>;
}
